<template>
  <div class="asset-view-wrapper">
    <div class="assets-table-container">
      <!-- KPI bar — identycznie jak Limonfy Zasoby: Razem | OK | Niska bateria | Długi postój | W logistyce -->
      <div
        v-if="!loading"
        class="wl-kpis"
      >
        <!-- 1. Zasoby razem — dark -->
        <div class="wl-stat wl-stat--dark">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasAssets.kpis.total') }}</span>
            <i class="bi bi-collection wl-stat__icon" />
          </div>
          <span class="wl-stat__value">{{ kpi.total }}</span>
        </div>

        <!-- 2. OK -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasAssets.kpis.ok') }}</span>
            <i
              class="bi bi-tag wl-stat__icon"
              style="color:#3f8f52"
            />
          </div>
          <span class="wl-stat__value">{{ kpi.ok }}</span>
          <div
            class="wl-stat__accent"
            :style="{ width: kpi.total ? `${(kpi.ok / kpi.total) * 100}%` : '0%', background: '#3f8f52' }"
          />
        </div>

        <!-- 3. Niska bateria -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasAssets.kpis.lowBatt') }}</span>
            <i
              class="bi bi-battery wl-stat__icon"
              style="color:#a07c20"
            />
          </div>
          <span class="wl-stat__value">{{ kpi.low_batt }}</span>
          <div
            class="wl-stat__accent"
            :style="{ width: kpi.total ? `${(kpi.low_batt / kpi.total) * 100}%` : '0%', background: '#a07c20' }"
          />
        </div>

        <!-- 4. Długi postój -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasAssets.kpis.dwell') }}</span>
            <i
              class="bi bi-clock-history wl-stat__icon"
              style="color:#4870b8"
            />
          </div>
          <span class="wl-stat__value">{{ kpi.dwell }}</span>
          <div
            class="wl-stat__accent"
            :style="{ width: kpi.total ? `${(kpi.dwell / kpi.total) * 100}%` : '0%', background: '#4870b8' }"
          />
        </div>

        <!-- 5. W tranzycie -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasAssets.kpis.transit') }}</span>
            <i
              class="bi bi-signpost-split wl-stat__icon"
              style="color:#4870b8"
            />
          </div>
          <span class="wl-stat__value">{{ kpi.in_transit }}</span>
          <div
            class="wl-stat__accent"
            :style="{ width: kpi.total ? `${(kpi.in_transit / kpi.total) * 100}%` : '0%', background: '#4870b8' }"
          />
        </div>

        <!-- 6. Wyszedł -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasAssets.kpis.lost') }}</span>
            <i
              class="bi bi-exclamation-triangle wl-stat__icon"
              style="color:#e8833a"
            />
          </div>
          <span class="wl-stat__value">{{ kpi.departed }}</span>
          <div
            class="wl-stat__accent"
            :style="{ width: kpi.total ? `${(kpi.departed / kpi.total) * 100}%` : '0%', background: '#e8833a' }"
          />
        </div>
      </div>

      <div class="assets-grid-card">
        <div class="assets-grid-card__header">
          <div class="assets-grid-title">
            <div class="assets-grid-title__icon">
              <i class="bi bi-broadcast-pin" />
            </div>
            <div>
              <div class="assets-grid-title__text">
                {{ $t('aside.menu.pages.wirepasAssets') }}
              </div>
              <div class="text-muted small">
                {{ $t('components.wirepasAssets.subtitle') }}
              </div>
            </div>
          </div>
          <div class="assets-grid-controls">
            <button
              type="button"
              class="assets-toolbar-btn"
              :title="$t('components.assets.dataGrids.refresh')"
              @click="refreshGrid"
            >
              <i class="bi bi-arrow-clockwise" />
              <span>{{ $t('components.assets.dataGrids.refresh') }}</span>
            </button>
          </div>
        </div>

        <div class="assets-grid-card__body">
          <!-- Wyszukiwarka — identycznie jak w Limonfy: osobny input nad filtrami -->
          <div class="wl-assets-search-wrap">
            <input
              v-model="searchQ"
              type="text"
              class="form-control wl-assets-search"
              :placeholder="$t('components.wirepasAssets.searchPlaceholder')"
              @input="onSearchInput"
            >
          </div>

          <!-- Filtry pill + dropdowny -->
          <div class="wl-assets-filters">
            <div class="wl-pills">
              <button
                v-for="f in statusFilters"
                :key="f.v"
                type="button"
                class="wl-pill"
                :class="{ 'wl-pill--active': statusFilter === f.v }"
                @click="statusFilter = f.v; refreshGrid()"
              >
                {{ f.l }}
              </button>
            </div>
            <div class="wl-select-wrap">
              <select
                v-model="categoryFilter"
                class="wl-select"
                @change="refreshGrid()"
              >
                <option value="all">
                  {{ $t('components.wirepasAssets.allCategories') }}
                </option>
                <option
                  v-for="(label, key) in categoryLabels"
                  :key="key"
                  :value="key"
                >
                  {{ label }}
                </option>
              </select>
              <i class="bi bi-chevron-down wl-select-icon" />
            </div>
          </div>

          <div
            v-if="error"
            class="alert alert-danger d-flex align-items-center gap-2 m-3"
          >
            <i class="bi bi-exclamation-triangle-fill" />
            <span>{{ error }}</span>
          </div>

          <div class="wp-grid-wrap">
            <div
              v-if="loading"
              class="wp-loading wp-loading--overlay"
            >
              <span
                class="spinner-border wp-loading__spinner"
                role="status"
              />
              <span class="wp-loading__text">{{ $t('components.wirepasAssets.loading') }}</span>
            </div>

            <DxDataGrid
              ref="gridRef"
              class="wirepas-assets-grid"
              :data-source="dataSource"
              :remote-operations="true"
              :hover-state-enabled="true"
              :column-auto-width="true"
              :show-borders="false"
              :show-column-lines="false"
              :show-row-lines="false"
              :row-alternation-enabled="true"
              :column-hiding-enabled="true"
              :word-wrap-enabled="false"
            >
              <DxLoadPanel :enabled="false" />
              <DxHeaderFilter :visible="true" />
              <DxPaging :page-size="20" />
              <DxPager
                :allowed-page-sizes="[10, 20, 50, 100]"
                :show-info="true"
                :show-page-size-selector="true"
                :show-navigation-buttons="true"
              />

              <DxColumn
                data-field="serialNumber"
                :caption="$t('components.wirepasAssets.columns.asset')"
                :flex="3"
                :min-width="160"
                cell-template="asset-template"
              />
              <DxColumn
                data-field="locationName"
                :caption="$t('components.wirepasAssets.columns.location')"
                :flex="2"
                :min-width="140"
                cell-template="location-template"
                :allow-sorting="false"
              />
              <DxColumn
                data-field="lastSeen"
                :caption="$t('components.wirepasAssets.columns.lastSeen')"
                width="140"
                cell-template="lastseen-template"
                sort-order="desc"
              />
              <DxColumn
                data-field="sinceAtLocation"
                :caption="$t('components.wirepasAssets.columns.sinceAtLocation')"
                width="200"
                :hiding-priority="1"
                cell-template="since-template"
                :allow-sorting="false"
              />
              <DxColumn
                data-field="gwId"
                :caption="$t('components.wirepasAssets.columns.gateway')"
                width="180"
                :hiding-priority="2"
                cell-template="gw-template"
              />
              <DxColumn
                data-field="status"
                :caption="$t('components.wirepasAssets.columns.status')"
                width="130"
                alignment="center"
                cell-template="status-template"
              />
              <DxColumn
                caption=""
                width="56"
                :allow-filtering="false"
                :allow-sorting="false"
                alignment="center"
                cell-template="actions-template"
              />

              <template #asset-template="{ data }">
                <span class="wl-asset-name">
                  <span
                    class="wl-asset-dot"
                    :class="data.data.active ? 'wl-asset-dot--active' : 'wl-asset-dot--inactive'"
                  />
                  <span class="font-monospace small">{{ data.data.name || data.data.serialNumber }}</span>
                  <span
                    class="wl-category-badge"
                    :class="`wl-category-badge--${data.data.assetCategory}`"
                  >{{ categoryLabels[data.data.assetCategory] || data.data.assetCategory }}</span>
                </span>
              </template>

              <template #location-template="{ data }">
                <span
                  v-if="data.data.location"
                  class="d-flex align-items-center gap-1"
                >
                  <span
                    v-if="data.data.location.type"
                    class="wirepas-type-badge"
                    :class="`wirepas-type-badge--${data.data.location.type}`"
                  >{{ locTypeLabel(data.data.location.type) }}</span>
                  {{ data.data.location.name }}
                </span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>

              <template #lastseen-template="{ data }">
                <span class="text-muted small font-monospace">{{ agoText(data.data.lastSeen) }}</span>
              </template>

              <template #since-template="{ data }">
                <span
                  v-if="data.data.sinceAtLocation"
                  class="text-muted small font-monospace"
                >
                  {{ durText(data.data.sinceAtLocation) }}
                  <template v-if="data.data.status !== 'departed'"> ({{ $t('components.wirepasAssets.ongoing') }})</template>
                </span>
                <span
                  v-else
                  class="text-muted"
                >—</span>
              </template>

              <template #gw-template="{ data }">
                <span
                  class="text-muted small font-monospace"
                  :title="data.data.gwId"
                >{{ shortGwId(data.data.gwId) }}</span>
              </template>

              <template #status-template="{ data }">
                <span
                  class="wl-status-badge"
                  :class="`wl-status-badge--${data.data.status}`"
                >
                  {{ $t(`components.wirepasAssets.status.${data.data.status}`) }}
                </span>
              </template>

              <template #actions-template="{ data }">
                <div
                  class="wl-row-actions"
                  style="justify-content:center"
                >
                  <button
                    type="button"
                    class="wl-row-action"
                    :title="$t('components.wirepasAssets.showDetail')"
                    @click.stop="$router.push(`/wirepas-assets/${data.data.id}`)"
                  >
                    <i class="bi bi-eye" />
                  </button>
                </div>
              </template>
            </DxDataGrid>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import CustomStore from 'devextreme/data/custom_store'
import {
  DxDataGrid,
  DxColumn,
  DxHeaderFilter,
  DxPaging,
  DxPager,
  DxLoadPanel,
} from 'devextreme-vue/data-grid'
import { getWirepasAssets, getWirepasAssetsStats } from '@/crud/wirepas-location'
import { wirepasAgoText, wirepasDurText, shortGwId as shortGwIdUtil, wirepasStatusFilters } from '@/utils/wirepas'

const LOC_TYPE_LABELS: Record<string, Record<string, string>> = {
  pl: { hala: 'Hala', crossdock: 'Cross-dock', sklep: 'Sklep' },
  en: { hala: 'Hall', crossdock: 'Cross-dock', sklep: 'Store' },
}

export default defineComponent({
  name: 'WirepasAssetsView',
  components: { DxDataGrid, DxColumn, DxHeaderFilter, DxPaging, DxPager, DxLoadPanel },
  emits: ['pageName'],

  setup(_, { emit }) {
    const { t, locale } = useI18n()
    const router = useRouter()
    const gridRef = ref<InstanceType<typeof DxDataGrid> | null>(null)
    const loading = ref(true)
    const error = ref<string | null>(null)
    const searchQ = ref('')
    const statusFilter = ref<'all' | 'ok' | 'low_batt' | 'in_transit' | 'departed'>('all')
    const categoryFilter = ref('all')
    let searchTimer: ReturnType<typeof setTimeout> | null = null

    const kpi = ref({ total: 0, ok: 0, low_batt: 0, dwell: 0, in_transit: 0, departed: 0 })

    const categoryLabels = computed<Record<string, string>>(() => ({
      pallet: t('components.wirepasLocations.assetCategories.pallet'),
      cart:   t('components.wirepasLocations.assetCategories.cart'),
      box:    t('components.wirepasLocations.assetCategories.box'),
      tag:    t('components.wirepasLocations.assetCategories.tag'),
      sensor: t('components.wirepasLocations.assetCategories.sensor'),
    }))

    const statusFilters = computed(() => wirepasStatusFilters(t))

    // Załaduj KPI osobno — niezależnie od paginacji
    const loadStats = async () => {
      try {
        const res = await getWirepasAssetsStats()
        const s = res.data
        kpi.value = {
          total:      s.total      ?? 0,
          ok:         s.ok         ?? 0,
          low_batt:   s.low_batt   ?? 0,
          dwell:      s.dwell      ?? 0,
          in_transit: s.in_transit ?? 0,
          departed:   s.departed   ?? 0,
        }
      } catch {
        // KPI nie blokuje głównego widoku
      }
    }

    const dataSource = new CustomStore({
      key: 'id',
      load: async (opts) => {
        loading.value = true
        error.value = null
        try {
          const params: any = {
            skip: opts.skip ?? 0,
            take: opts.take ?? 20,
          }
          if (searchQ.value.trim()) params.searchValue = searchQ.value.trim()
          if (statusFilter.value !== 'all') params.statusFilter = statusFilter.value
          if (categoryFilter.value !== 'all') params.categoryFilter = categoryFilter.value

          const res = await getWirepasAssets(params)
          const rows: any[] = res.data?.data ?? []
          const total: number = res.data?.totalCount ?? rows.length

          return { data: rows.map(r => ({ ...r, locationName: r.location?.name ?? null })), totalCount: total }
        } catch (e: any) {
          error.value = e?.response?.data?.error || e?.message || t('components.wirepasAssets.error')
          throw e
        } finally {
          loading.value = false
        }
      },
    })

    // Odświeżenie KPI przy refresh gridu
    const origRefresh = () => gridRef.value?.instance?.refresh()

    const refreshGrid = () => {
      loadStats()
      origRefresh()
    }

    const onRowClick = (e: any) => {
      const id = e.data?.id
      if (id) router.push(`/wirepas-assets/${id}`)
    }

    const onSearchInput = () => {
      if (searchTimer) clearTimeout(searchTimer)
      searchTimer = setTimeout(() => { origRefresh() }, 300)
    }

    const setTitle = () => emit('pageName', t('aside.menu.pages.wirepasAssets'))

    const locTypeLabel = (type: string): string => {
      const lang = locale.value
      return LOC_TYPE_LABELS[lang]?.[type] ?? LOC_TYPE_LABELS['pl'][type] ?? type
    }

    const shortGwId = shortGwIdUtil
    const agoText = (tsSec: number | null) => wirepasAgoText(tsSec, t)
    const durText = (sinceSec: number | null) => wirepasDurText(sinceSec)

    setTitle()
    onMounted(loadStats)

    return {
      gridRef,
      loading,
      error,
      kpi,
      dataSource,
      searchQ,
      statusFilter,
      categoryFilter,
      categoryLabels,
      statusFilters,
      refreshGrid,
      onSearchInput,
      onRowClick,
      locTypeLabel,
      agoText,
      durText,
      shortGwId,
    }
  },

  watch: {
    '$i18n.locale'() {
      this.$emit('pageName', this.$t('aside.menu.pages.wirepasAssets'))
    },
  },
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/wirepas-locations-view.scss';

.wl-assets-search-wrap {
  padding: 14px 16px 0;

  .wl-assets-search {
    max-width: 380px;
    border-radius: 10px;
    font-size: 13px;
  }
}

.wl-assets-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.wl-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.wl-select {
  appearance: none;
  -webkit-appearance: none;
  padding: 5px 32px 5px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  background: transparent;
  color: var(--text-color, #555);
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: all 0.12s;
  outline: none;

  &:hover {
    border-color: rgba(var(--primary-color-rgb), 0.4);
    color: var(--primary-color);
  }

  &:focus {
    border-color: var(--primary-color);
    color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.12);
  }
}

.wl-select-icon {
  position: absolute;
  right: 11px;
  font-size: 10px;
  color: var(--text-muted, #888);
  pointer-events: none;
  transition: color 0.12s;

  .wl-select:hover ~ &,
  .wl-select:focus ~ & {
    color: var(--primary-color);
  }
}

.wl-pills {
  display: flex;
  gap: 6px;
}

.wl-pill {
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  background: transparent;
  color: var(--text-color, #555);
  cursor: pointer;
  transition: all 0.12s;

  &:hover { border-color: rgba(var(--primary-color-rgb), 0.4); color: var(--primary-color); }
  &--active { background: var(--primary-color); border-color: var(--primary-color); color: #ffffff; }
}

.wl-asset-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.wl-asset-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &--active   { background: #3f8f52; }
  &--inactive { background: rgba(108, 117, 125, 0.35); }
}

.dx-grid-clickable :deep(.dx-data-row) { cursor: pointer; }

.wirepas-assets-grid :deep(.dx-datagrid-headers .dx-header-row td) {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(108, 117, 125, 0.9);
}
</style>
